import config from '../config'

export interface LabelNode {
  id: number
  project_id?: number
  parent_id?: number
  name: string
  type_id: number
  path: string
  depth: number
  description?: string
  created_at: string
   // 백엔드에서 주입하는 리소스 권한(actions) – 선택적
  actions?: { [key: string]: boolean }
  children?: LabelNode[]
}

export interface LabelSetStatus {
  label_set_id: number
  status: string
  total: number
  matched: number
  unmatched: number
  duplicates?: number
  fail_reason?: string | null
  fail_message?: string | null
  is_test_locked?: boolean
}

export interface LabelSetItem {
  id: number
  label_rel_path: string
  stem: string
  match_status: 'matched' | 'unmatched' | 'duplicate'
  matched_raw_rel_path?: string | null
}

export interface LabelSetSource {
  id?: number
  label_set_id?: number
  raw_node_id: number
  raw_batch_id: number | null
  created_at?: string
  project_name?: string
  group_name?: string
  stream_name?: string
  full_path_name?: string
  batch_display_name?: string
}

export interface LabelSetMeta {
  version_node_id: number
  label_set_id: number
  format: string
  archive_filename?: string | null
  archive_size_bytes?: number | null
  created_at?: string | null
  task_type?: string | null
}

class LabelService {
  private baseUrl = config.API_BASE_URL

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token')
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }

  async createLabelProject(data: { name: string; group?: string; version?: string; projectDescription?: string; groupDescription?: string; versionDescription?: string; status?: string; existingProjectId?: number }): Promise<LabelNode> {
    // 기존 프로젝트를 선택한 경우: 경로 기반 생성 API 사용
    if (data.existingProjectId && data.name) {
      const parts = [data.name]
      if (data.group) parts.push(data.group)
      if (data.version) parts.push(data.version)
      const resp = await fetch(`${this.baseUrl}/labels/projects/${data.existingProjectId}/create-by-path`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          path_parts: parts,
          group_description: data.groupDescription || undefined,
          version_description: data.versionDescription || undefined,
        })
      })
      if (!resp.ok) {
        const txt = await resp.text()
        throw new Error(txt || `라벨 경로 생성 실패(${resp.status})`)
      }
      return resp.json()
    }
    const payload: any = {
      name: data.name || undefined,
      group: data.group || undefined,
      version: data.version || undefined,
      project_description: data.projectDescription || undefined,
      group_description: data.groupDescription || undefined,
      version_description: data.versionDescription || undefined,
      status: data.status || 'active'
    }
    const resp = await fetch(`${this.baseUrl}/labels/projects`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload)
    })
    if (!resp.ok) {
      const txt = await resp.text()
      throw new Error(txt || `라벨 프로젝트 생성 실패(${resp.status})`)
    }
    return resp.json()
  }

  async getRootNodes(): Promise<LabelNode[]> {
    const resp = await fetch(`${this.baseUrl}/labels/nodes/root`, { headers: this.getAuthHeaders() })
    if (!resp.ok) return []
    return resp.json()
  }

  async getNodeChildren(nodeId: number): Promise<LabelNode[]> {
    const resp = await fetch(`${this.baseUrl}/labels/nodes/${nodeId}/children`, { headers: this.getAuthHeaders() })
    if (!resp.ok) return []
    return resp.json()
  }

  async uploadLabelSet(params: { versionNodeId: number; rawNodeId: number; file: File; rawBatchId?: number | null; rawSources?: Array<{ raw_node_id: number; raw_batch_id?: number | null }>; taskType?: 'classification' | 'detection' | 'segmentation' }): Promise<{ label_set_id: number; status: string; message: string }> {
    const form = new FormData()
    form.append('raw_node_id', String(params.rawNodeId))
    if (params.rawBatchId != null) form.append('raw_batch_id', String(params.rawBatchId))
    if (params.rawSources && params.rawSources.length > 0) {
      try { form.append('raw_sources', JSON.stringify(params.rawSources)) } catch {}
    }
    if (params.taskType) {
      form.append('task_type', params.taskType)
    }
    form.append('upload', params.file, params.file.name)
    const resp = await fetch(`${this.baseUrl}/labels/sets/${params.versionNodeId}/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
      body: form
    })
    if (!resp.ok) {
      const txt = await resp.text()
      throw new Error(txt || `라벨 업로드 실패(${resp.status})`)
    }
    return resp.json()
  }

  async getLabelSetStatus(versionNodeId: number): Promise<LabelSetStatus | null> {
    const resp = await fetch(`${this.baseUrl}/labels/sets/${versionNodeId}/status`, { headers: this.getAuthHeaders() })
    if (!resp.ok) return null
    return resp.json()
  }

  async listLabelSetItems(versionNodeId: number, status?: 'matched' | 'unmatched' | 'duplicate', skip: number = 0, limit: number = 200): Promise<LabelSetItem[]> {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    params.set('skip', String(skip))
    params.set('limit', String(limit))
    const resp = await fetch(`${this.baseUrl}/labels/sets/${versionNodeId}/items?${params.toString()}`, { headers: this.getAuthHeaders() })
    if (!resp.ok) return []
    return resp.json()
  }

  async listLabelSetSources(labelSetId: number): Promise<LabelSetSource[]> {
    const resp = await fetch(`${this.baseUrl}/labels/sets/${labelSetId}/sources`, { headers: this.getAuthHeaders() })
    if (!resp.ok) return []
    return resp.json()
  }

  async getLabelSetMeta(versionNodeId: number): Promise<LabelSetMeta | null> {
    const resp = await fetch(`${this.baseUrl}/labels/sets/${versionNodeId}/meta`, { headers: this.getAuthHeaders() })
    if (!resp.ok) return null
    return resp.json()
  }

  async deleteLabelSet(versionNodeId: number): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/labels/sets/${versionNodeId}`, { method: 'DELETE', headers: this.getAuthHeaders() })
    if (!resp.ok) {
      let message = `라벨 세트 삭제 실패(${resp.status})`
      try {
        // FastAPI 기본 에러 포맷: { detail: string | object }
        const data = await resp.json()
        if (data && typeof data.detail === 'string' && data.detail.trim().length > 0) {
          message = data.detail
        }
      } catch {
        try { message = await resp.text() } catch {}
      }
      const error: any = new Error(message)
      error.status = resp.status
      throw error
    }
  }

  async abortLabelSet(labelSetId: number, message: string = 'aborted by user'): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/labels/sets/${labelSetId}/fail`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ message, reason: 'user_abort' })
    })
    if (!resp.ok) {
      const txt = await resp.text()
      throw new Error(txt || `라벨 세트 중단 실패(${resp.status})`)
    }
  }

  async retryLabelSet(labelSetId: number): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/labels/sets/${labelSetId}/retry`.replace(`${labelSetId}`, String(labelSetId)), {
      method: 'POST',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) {
      const txt = await resp.text()
      throw new Error(txt || `라벨 세트 재시작 실패(${resp.status})`)
    }
  }
}

export default new LabelService()



